import { useCallback, useEffect, useState } from "react";

/**
 * Per-tab "view as parent" mode for coaches.
 *
 * Presentation only — it never widens what the server allows, it just narrows
 * what a coach sees so they can preview the page the way a linked parent does
 * without being routed away to the parent dashboard.
 *
 * Stored in sessionStorage (per tab, resets on a new tab) and broadcast via a
 * window event so the header toggle and the page stay in sync.
 */
const KEY = "viewAsParent";
const EVENT = "viewasparent:change";

function read(): boolean {
  try {
    return sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function useViewAsParent() {
  const [viewAsParent, setState] = useState<boolean>(read);

  useEffect(() => {
    const handler = () => setState(read());
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  const setViewAsParent = useCallback((next: boolean) => {
    try {
      if (next) sessionStorage.setItem(KEY, "1");
      else sessionStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new Event(EVENT));
  }, []);

  const toggle = useCallback(() => setViewAsParent(!read()), [setViewAsParent]);

  return { viewAsParent, setViewAsParent, toggle };
}

/** Routes where the parent toggle switches view in place instead of navigating. */
export function supportsInPageParentView(pathname: string): boolean {
  return /^\/(rsvp|game)\//.test(pathname);
}
